import { useEventManagement } from "components/identifyEventsTool/hooks/useEventManagement";
import { AreaLabels, ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import {
  useAreaLabelledEvent,
  useAreaLabelledEvents,
} from "components/scrapEyeLabelling/hooks/useAreaLabel";
import {
  addEventMetadata,
  extractLabels,
  isMassEventDetached,
} from "components/manualReview/utils/formatters";
import {
  dedupeReviewEvents,
  stableReviewEventId,
} from "components/manualReview/utils/eventUtils";
import {
  toClientIdentifiedReviewEvent,
  withEditableReviewLabels,
  savedEditableLabels,
  ReviewEditableLabels,
} from "components/manualReview/utils/reviewSerialization";
import { reviewContentKey } from "components/manualReview/utils/eventUtils";
import {
  eventsEditedAgainstSeed,
  groupLiftDropSequences,
  maxSequenceGapSecondsFromChunkSeconds,
} from "components/manualReview/utils/editScope";
import { sorted } from "helpers";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";

dayjs.extend(utc);
import { DefaultAreas, ReviewSlotRevision } from "src/store/api/generatedApi";

import {
  createContext,
  ReactNode,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { EventType, ReviewEvent } from "src/store/api/generatedApi";
import { useUndoRedo } from "hooks/useUndoRedo";
import {
  MaterialEditScope,
  UpdateBody,
} from "components/identifyEventsTool/hooks/useEventManagement";

export type ManualReviewEventContextValue = {
  allEvents: ScrapEyesEventWithReviewData[];
  reviewSlotRevisions: ReviewSlotRevision[] | undefined;
  refreshedEventIds: ReadonlyMap<string, string>;
  clearLocalChanges: () => void;
  rememberSavedEvents: (events: ScrapEyesEventWithReviewData[]) => void;
  revertEvent: (id: string) => void;
  videoStart: Dayjs;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  writableStart: Dayjs | undefined;
  writableEnd: Dayjs | undefined;
  canWrite: (eventId: string, proposedStart?: Dayjs) => boolean;
  handleUndo: () => void;
  handleRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  modifiedEventIds: Set<string>;
  hasUnsavedChanges: boolean;
  originalEventsById: Map<string, ScrapEyesEventWithReviewData>;
  // The events the provider was seeded with, keyed by id — the fetched set overlaid
  // with any ancestor edits (`seedOverrides`). The baseline for what counts as an edit
  // made here, as opposed to one already present when the provider opened.
  seedEventsById: Map<string, ScrapEyesEventWithReviewData>;
  // Whether the reviewer has changed anything on this surface: a diff against the seed,
  // so edits an ancestor surface carried in do not read as made here.
  editedSinceSeed: boolean;
} & Omit<
  ReturnType<typeof useEventManagement<ScrapEyesEventWithReviewData>>,
  "events" | "editableEvents" | "setEvents" | "clearEvents"
>;

const ManualReviewEventContext = createContext<
  ManualReviewEventContextValue | undefined
>(undefined);

export const useManualReviewEvents = (): ManualReviewEventContextValue => {
  const context = use(ManualReviewEventContext);
  if (!context) {
    throw new Error(
      "useManualReviewEvents must be used within ManualReviewEventProvider"
    );
  }
  return context;
};

export type ScrapEyesEventWithReviewData = ScrapEyesEvent &
  Omit<ReviewEvent, "merged"> & {
    resolved?: boolean;
    massDetached?: boolean;
    persistedReview?: {
      contents: ReviewEvent["merged"];
      baseline: ReturnType<typeof extractLabels>;
      saved: boolean;
      editableLabels?: ReviewEditableLabels;
    };
  };

const defaultConstructor = (
  event: ScrapEyesEvent
): ScrapEyesEventWithReviewData => {
  return {
    ...event,
    original_events: [],
    mass_outlier_band: null,
    review_reasons: [],
    mass_event: null,
    resolved: false,
  };
};

export const fromReviewEvent = (
  event: ReviewEvent,
  defaultAreas: DefaultAreas
): Omit<ScrapEyesEventWithReviewData, keyof AreaLabels> => {
  const labels = extractLabels(
    event.merged.labels,
    event.merged.event.event_type,
    defaultAreas
  );
  const {
    material,
    coMaterial,
    area,
    comment,
    mass,
    initialMass,
    finalMass,
    confidences,
  } = labels;

  return {
    persistedReview: {
      contents: event.merged,
      baseline: labels,
      saved: event.original_events.length === 0,
    },
    original_events: event.original_events,
    review_reasons: event.review_reasons,
    mass_event: event.mass_event,
    mass_outlier_band: event.mass_outlier_band,
    id: stableReviewEventId(event),
    start: dayjs.utc(event.merged.event.start),
    end: dayjs.utc(event.merged.event.end),
    eventType: event.merged.event.event_type,
    material,
    coMaterial,
    area,
    comment,
    mass,
    initialMass,
    finalMass,
    confidences,
    massDetached: isMassEventDetached({
      hasMatchedMassEvent: event.mass_event !== null,
      mass,
    }),
  };
};

// Maps a fetched, read-only set of events (a neighbour or adjacent-period preview,
// rendered outside any page's own event manager) the same way every such preview
// does: seed pairId as null so lift/drops re-pair from scratch, drawing the pair
// connector and mismatch marker independently of whatever pairing the page's own
// live event context has.
export const mapReadOnlyPreviewEvents = ({
  events,
  defaultAreas,
  withAreaLabels,
}: {
  events: ReviewEvent[];
  defaultAreas: DefaultAreas;
  withAreaLabels: ReturnType<typeof useAreaLabelledEvents>;
}): ScrapEyesEventWithReviewData[] =>
  addEventMetadata(
    withAreaLabels(
      dedupeReviewEvents(events).map((event) => ({
        ...fromReviewEvent(event, defaultAreas),
        pairId: null,
      }))
    ),
    defaultAreas
  );

// Overlays an ancestor surface's unsaved edits onto the fetched seed for the same
// region: events in `omittedOriginalEventIds` are dropped, `seedOverrides` replace
// the fetched events sharing their id and append any that are new, and the result is
// ordered by start so an event a drag moved re-sorts and adjacent-pair detection holds.
export const applySeedOverrides = ({
  fetched,
  seedOverrides,
  omittedOriginalEventIds,
}: {
  fetched: ScrapEyesEventWithReviewData[];
  seedOverrides: ScrapEyesEventWithReviewData[] | undefined;
  omittedOriginalEventIds: ReadonlySet<string> | undefined;
}): ScrapEyesEventWithReviewData[] => {
  if (
    (seedOverrides === undefined || seedOverrides.length === 0) &&
    omittedOriginalEventIds === undefined
  ) {
    return fetched;
  }
  const kept = omittedOriginalEventIds
    ? fetched.filter((event) => !omittedOriginalEventIds.has(event.id))
    : fetched;
  if (seedOverrides === undefined || seedOverrides.length === 0) {
    return kept;
  }
  const overrideById = new Map(seedOverrides.map((event) => [event.id, event]));
  const keptIds = new Set(kept.map((event) => event.id));
  const merged = [
    ...kept.map((event) => overrideById.get(event.id) ?? event),
    ...seedOverrides.filter((event) => !keptIds.has(event.id)),
  ];
  return sorted(merged, (event) => event.start.valueOf());
};

/** Whether an event differs from a baseline copy — the edits the reviewer can make. A
 * missing baseline (a new id) counts as differing. */
export const eventDiffersFromBaseline = (
  displayedEvent: ScrapEyesEventWithReviewData,
  displayedBaseline: ScrapEyesEventWithReviewData | undefined
): boolean => {
  if (displayedBaseline === undefined) return true;
  const event = withEditableReviewLabels(displayedEvent);
  const baseline = withEditableReviewLabels(displayedBaseline);
  return (
    event.eventType !== baseline.eventType ||
    (event.material ?? null) !== (baseline.material ?? null) ||
    (event.coMaterial ?? null) !== (baseline.coMaterial ?? null) ||
    (event.area ?? null) !== (baseline.area ?? null) ||
    (event.comment ?? null) !== (baseline.comment ?? null) ||
    (event.mass ?? null) !== (baseline.mass ?? null) ||
    (event.initialMass ?? null) !== (baseline.initialMass ?? null) ||
    (event.finalMass ?? null) !== (baseline.finalMass ?? null) ||
    (event.resolved ?? false) !== (baseline.resolved ?? false) ||
    (event.massDetached ?? false) !== (baseline.massDetached ?? false) ||
    !event.start.isSame(baseline.start) ||
    !event.end.isSame(baseline.end)
  );
};

export const ManualReviewEventProvider = ({
  events: externalEvents,
  reviewSlotRevisions,
  videoStart,
  windowStart,
  windowEnd,
  writableStart,
  writableEnd,
  seedOverrides,
  omittedOriginalEventIds,
  location,
  areaMaterialStorageKey,
  children,
}: {
  events: ReviewEvent[];
  reviewSlotRevisions?: ReviewSlotRevision[];
  videoStart: Dayjs;
  windowStart: Dayjs;
  windowEnd: Dayjs;
  // Events starting outside [writableStart, writableEnd) are read-only on this
  // surface: update, delete, revert, material, area and the pair type toggle
  // refuse them, the edit modal opens read-only, and new, dragged or picked
  // timestamps clamp into the range. Omitted, every guard is inert.
  writableStart?: Dayjs;
  writableEnd?: Dayjs;
  // Internal events that replace or extend the fetched seed by id, and original
  // ids to drop from it: the current, unsaved edits an ancestor review surface
  // holds for the region this provider re-fetches.
  seedOverrides?: ScrapEyesEventWithReviewData[];
  omittedOriginalEventIds?: ReadonlySet<string>;
  location: string | undefined;
  areaMaterialStorageKey?: string | null;
  children: ReactNode;
}) => {
  const { default_areas: defaultAreas, reservation } = useScrapEyeMetadata();
  const maxSequenceGapSeconds = maxSequenceGapSecondsFromChunkSeconds(
    reservation.chunk_seconds
  );
  const findSequenceMemberIds = useCallback(
    (events: ScrapEyesEventWithReviewData[], eventId: string): string[] =>
      (
        groupLiftDropSequences({ events, maxSequenceGapSeconds }).find(
          (sequence) => sequence.some((event) => event.id === eventId)
        ) ?? []
      )
        .map((event) => event.id)
        .filter((id) => id !== eventId),
    [maxSequenceGapSeconds]
  );
  const withAreaLabels = useAreaLabelledEvents(location);
  const withAreaLabel = useAreaLabelledEvent(location);

  const {
    events: internalEvents,
    editableEvents,
    setEvents,
    recordingEventId,
    selectedEventId,
    handleSelectEvent,
    setSelectedEventId,
    handleUpdateEvent: rawHandleUpdateEvent,
    handleDeleteEvent: rawHandleDeleteEvent,
    handleAddFlag: rawHandleAddFlag,
    handleStartRecording: rawHandleStartRecording,
    handleStopRecording: rawHandleStopRecording,
    handleSetMaterial: rawHandleSetMaterial,
    handleSetArea: rawHandleSetArea,
    handleReverseEventType: rawHandleReverseEventType,
    handleSnapToCandidate: rawHandleSnapToCandidate,
    ...restEventManagement
  } = useEventManagement<ScrapEyesEventWithReviewData>(
    null,
    videoStart,
    defaultConstructor,
    "independent",
    undefined, // candidateEvents
    undefined, // locationKey
    writableEnd, // lastPeriodEnd
    undefined, // labelRecordingPlaybackRate
    writableStart, // firstPeriodStart
    areaMaterialStorageKey,
    findSequenceMemberIds
  );

  const undoRedo = useUndoRedo<ScrapEyesEventWithReviewData>();

  const editableEventsRef = useRef(editableEvents);
  editableEventsRef.current = editableEvents;

  const serializableEventsRef = useRef<ScrapEyesEventWithReviewData[]>([]);
  const eventsRef = useRef(internalEvents);
  eventsRef.current = internalEvents;

  const recordingEventIdRef = useRef(recordingEventId);
  recordingEventIdRef.current = recordingEventId;

  const selectEventRef = useRef(setSelectedEventId);
  selectEventRef.current = setSelectedEventId;

  const selectedEventIdRef = useRef(selectedEventId);
  selectedEventIdRef.current = selectedEventId;

  const postRecordingEventIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!recordingEventId) return;
    if (internalEvents.some((event) => event.id === recordingEventId)) return;
    // An involuntary stop has no post-recording edit coming, and an earlier
    // recording's marker left set would swallow the next edit's undo step.
    postRecordingEventIdRef.current = null;
    rawHandleStopRecording();
  }, [recordingEventId, internalEvents, rawHandleStopRecording]);

  const mappedExternalEvents = useMemo(
    () =>
      withAreaLabels(
        dedupeReviewEvents(externalEvents).map((event) =>
          fromReviewEvent(event, defaultAreas)
        )
      ),
    [externalEvents, defaultAreas, withAreaLabels]
  );

  const seed = useMemo(
    () =>
      applySeedOverrides({
        fetched: mappedExternalEvents,
        seedOverrides,
        omittedOriginalEventIds,
      }).map(withEditableReviewLabels),
    [mappedExternalEvents, seedOverrides, omittedOriginalEventIds]
  );
  const seedRef = useRef(seed);
  seedRef.current = seed;

  // Which seed the current events were set from. Between a seed arriving and the effect
  // applying it, the events still belong to the previous one, so anything comparing the two
  // would read the gap as the reviewer's own edit.
  const appliedSeedRef = useRef<ScrapEyesEventWithReviewData[] | null>(null);
  const refreshedEventIdsRef = useRef(new Map<string, string>());
  const savedContentKeysRef = useRef(new Map<string, string>());
  const rememberSavedEvents = useCallback(
    (events: ScrapEyesEventWithReviewData[]) => {
      savedContentKeysRef.current = new Map(
        events.map((event) => {
          const serialized = toClientIdentifiedReviewEvent(event);
          return [
            event.id,
            reviewContentKey(serialized.event, serialized.labels),
          ];
        })
      );
    },
    []
  );

  useEffect(() => {
    const freshIds = new Set(seed.map((event) => event.id));
    const freshByContent = new Map(
      seed
        .filter((event) => event.original_events.length === 0)
        .map((event) => {
          const serialized = toClientIdentifiedReviewEvent(event);
          return [
            reviewContentKey(serialized.event, serialized.labels),
            event.id,
          ];
        })
    );
    const replacements = new Map(
      serializableEventsRef.current.flatMap((event) => {
        if (freshIds.has(event.id)) return [[event.id, event.id] as const];
        const submittedContent = savedContentKeysRef.current.get(event.id);
        if (event.original_events.length > 0 && submittedContent === undefined)
          return [];
        const serialized = toClientIdentifiedReviewEvent(event);
        const nextId = freshByContent.get(
          submittedContent ??
            reviewContentKey(serialized.event, serialized.labels)
        );
        if (nextId !== undefined) savedContentKeysRef.current.delete(event.id);
        return nextId === undefined ? [] : [[event.id, nextId] as const];
      })
    );
    refreshedEventIdsRef.current = new Map([
      ...[...refreshedEventIdsRef.current].map(
        ([previous, current]) =>
          [previous, replacements.get(current) ?? current] as const
      ),
      ...replacements,
    ]);
    const selected = selectedEventIdRef.current;
    if (selected !== null)
      selectEventRef.current(
        replacements.get(selected) ??
          (seed.some((event) => event.id === selected) ? selected : null)
      );
    setEvents(seed);
    undoRedo.reset();
    appliedSeedRef.current = seed;
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable (changes on canUndo/canRedo); use the stable .reset method
  }, [seed, setEvents, undoRedo.reset]);

  const clearLocalChanges = useCallback(() => {
    setEvents(seedRef.current);
    undoRedo.reset();
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .reset method
  }, [setEvents, undoRedo.reset]);

  // Every snapshot is of the whole event array, so a step captured mid-recording
  // pins the recording at the end time it had then, and undoing to it hands back
  // a truncated event. The whole hold is therefore one step.
  const shouldCaptureUndoStep = useCallback((): boolean => {
    const recordingEventId = recordingEventIdRef.current;
    if (!recordingEventId) return true;
    return !eventsRef.current.some((event) => event.id === recordingEventId);
  }, []);

  const canWrite = useCallback(
    (eventId: string, proposedStart?: Dayjs): boolean => {
      if (writableStart === undefined && writableEnd === undefined) return true;
      const inRange = (start: Dayjs) =>
        (writableStart === undefined || !start.isBefore(writableStart)) &&
        (writableEnd === undefined || start.isBefore(writableEnd));
      const target = eventsRef.current.find((event) => event.id === eventId);
      return target !== undefined && inRange(proposedStart ?? target.start);
    },
    [writableStart, writableEnd]
  );

  const revertEvent = useCallback(
    (id: string) => {
      if (!canWrite(id)) return;
      const originalExternal = externalEvents.find(
        (event) => stableReviewEventId(event) === id
      );
      if (!originalExternal) return;
      const originalInternal = withAreaLabel(
        fromReviewEvent(originalExternal, defaultAreas)
      );
      if (shouldCaptureUndoStep()) {
        undoRedo.capture(editableEventsRef.current);
      }
      setEvents(
        editableEventsRef.current.map((event) =>
          event.id === id ? originalInternal : event
        )
      );
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .capture method
    [
      canWrite,
      externalEvents,
      defaultAreas,
      undoRedo.capture,
      setEvents,
      withAreaLabel,
    ]
  );

  const captureAndRun = useCallback(
    <Args extends unknown[]>(fn: (...args: Args) => void) =>
      (...args: Args) => {
        postRecordingEventIdRef.current = null;
        if (shouldCaptureUndoStep()) {
          undoRedo.capture(editableEventsRef.current);
        }
        fn(...args);
      },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .capture method
    [undoRedo.capture, shouldCaptureUndoStep]
  );

  const handleUpdateEvent = useCallback(
    (
      id: string,
      updates: UpdateBody<ScrapEyesEventWithReviewData>,
      materialEditScope?: MaterialEditScope
    ) => {
      if (!canWrite(id, updates.start)) return;
      const isPostRecordingUpdate = id === postRecordingEventIdRef.current;
      postRecordingEventIdRef.current = null;
      if (!isPostRecordingUpdate && shouldCaptureUndoStep()) {
        undoRedo.capture(editableEventsRef.current);
      }
      rawHandleUpdateEvent(id, updates, materialEditScope);
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .capture method
    [canWrite, undoRedo.capture, rawHandleUpdateEvent, shouldCaptureUndoStep]
  );

  const handleDeleteEvent = useCallback(
    (id: string) => {
      if (!canWrite(id)) return;
      captureAndRun(rawHandleDeleteEvent)(id);
    },
    [canWrite, captureAndRun, rawHandleDeleteEvent]
  );

  const handleAddFlag = useCallback(() => {
    captureAndRun(rawHandleAddFlag)();
  }, [captureAndRun, rawHandleAddFlag]);

  const handleStartRecording = useCallback(
    (...args: Parameters<typeof rawHandleStartRecording>) => {
      if (!recordingEventIdRef.current) {
        undoRedo.capture(editableEventsRef.current);
      }
      rawHandleStartRecording(...args);
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .capture method
    [undoRedo.capture, rawHandleStartRecording]
  );

  const handleStopRecording = useCallback(
    (eventType?: EventType) => {
      postRecordingEventIdRef.current = recordingEventIdRef.current;
      rawHandleStopRecording(eventType);
    },
    [rawHandleStopRecording]
  );

  const handleSetMaterial = useCallback(
    (material: string | null) => {
      const targetEventId =
        recordingEventIdRef.current ?? selectedEventIdRef.current;
      if (targetEventId !== null && !canWrite(targetEventId)) return;
      captureAndRun(rawHandleSetMaterial)(material);
    },
    [canWrite, captureAndRun, rawHandleSetMaterial]
  );

  const handleSetArea = useCallback(
    (area: string | null) => {
      const targetEventId =
        recordingEventIdRef.current ?? selectedEventIdRef.current;
      if (targetEventId !== null && !canWrite(targetEventId)) return;
      captureAndRun(rawHandleSetArea)(area);
    },
    [canWrite, captureAndRun, rawHandleSetArea]
  );

  const handleReverseEventType = useCallback(() => {
    const targetEventId = selectedEventIdRef.current;
    const target =
      targetEventId === null
        ? undefined
        : eventsRef.current.find((event) => event.id === targetEventId);
    if (target !== undefined) {
      const members =
        target.pairId != null
          ? eventsRef.current.filter((event) => event.pairId === target.pairId)
          : [target];
      if (members.some((member) => !canWrite(member.id))) return;
    }
    captureAndRun(rawHandleReverseEventType)();
  }, [canWrite, captureAndRun, rawHandleReverseEventType]);

  const handleSnapToCandidate = useCallback(() => {
    captureAndRun(rawHandleSnapToCandidate)();
  }, [captureAndRun, rawHandleSnapToCandidate]);

  const originalEventsById = useMemo(
    () => new Map(mappedExternalEvents.map((event) => [event.id, event])),
    [mappedExternalEvents]
  );

  const modifiedEventIds = useMemo(
    () =>
      new Set(
        editableEvents
          .filter((event) =>
            eventDiffersFromBaseline(event, originalEventsById.get(event.id))
          )
          .map((event) => event.id)
      ),
    [editableEvents, originalEventsById]
  );

  const seedEventsById = useMemo(
    () => new Map(seed.map((event) => [event.id, event])),
    [seed]
  );

  const editedSinceSeed = useMemo(
    () =>
      appliedSeedRef.current === seed &&
      eventsEditedAgainstSeed({
        events: editableEvents,
        seedEvents: seed,
        seedById: seedEventsById,
        eventDiffersFromSeed: eventDiffersFromBaseline,
      }),
    [editableEvents, seed, seedEventsById]
  );

  // A modified or added event lands in modifiedEventIds; a deletion is an
  // original id no longer present among the current events, so it must be
  // checked separately.
  const hasUnsavedChanges = useMemo(() => {
    if (modifiedEventIds.size > 0) return true;
    const currentEventIds = new Set(internalEvents.map((event) => event.id));
    return Array.from(originalEventsById.keys()).some(
      (id) => !currentEventIds.has(id)
    );
  }, [modifiedEventIds, internalEvents, originalEventsById]);

  const handleUndo = useCallback(() => {
    const previous = undoRedo.undo(editableEventsRef.current);
    if (previous) setEvents(previous);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .undo method
  }, [undoRedo.undo, setEvents]);

  const handleRedo = useCallback(() => {
    const next = undoRedo.redo(editableEventsRef.current);
    if (next) setEvents(next);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- undoRedo object is unstable; use the stable .redo method
  }, [undoRedo.redo, setEvents]);

  const eventsWithMetadata = useMemo(() => {
    const editableById = new Map(
      editableEvents.map((event) => [event.id, event])
    );
    const displayed = internalEvents.map((event) => {
      const editable = editableById.get(event.id);
      return event.persistedReview?.saved && editable
        ? {
            ...event,
            persistedReview: {
              ...event.persistedReview,
              editableLabels: savedEditableLabels(editable),
            },
          }
        : event;
    });
    return addEventMetadata(withAreaLabels(displayed), defaultAreas);
  }, [internalEvents, editableEvents, defaultAreas, withAreaLabels]);

  serializableEventsRef.current = eventsWithMetadata;

  const contextValue: ManualReviewEventContextValue = useMemo(
    () => ({
      allEvents: eventsWithMetadata,
      reviewSlotRevisions,
      refreshedEventIds: refreshedEventIdsRef.current,
      handleSelectEvent,
      setSelectedEventId,
      rememberSavedEvents,
      clearLocalChanges,
      revertEvent,
      videoStart,
      windowStart,
      windowEnd,
      writableStart,
      writableEnd,
      canWrite,
      handleUpdateEvent,
      handleDeleteEvent,
      handleAddFlag,
      handleStartRecording,
      handleStopRecording,
      handleSetMaterial,
      handleSetArea,
      handleReverseEventType,
      handleSnapToCandidate,
      handleUndo,
      handleRedo,
      canUndo: undoRedo.canUndo,
      canRedo: undoRedo.canRedo,
      modifiedEventIds,
      hasUnsavedChanges,
      originalEventsById,
      seedEventsById,
      editedSinceSeed,
      selectedEventId,
      recordingEventId,
      ...restEventManagement,
    }),
    [
      eventsWithMetadata,
      reviewSlotRevisions,
      handleSelectEvent,
      setSelectedEventId,
      rememberSavedEvents,
      clearLocalChanges,
      revertEvent,
      videoStart,
      windowStart,
      windowEnd,
      writableStart,
      writableEnd,
      canWrite,
      handleUpdateEvent,
      handleDeleteEvent,
      handleAddFlag,
      handleStartRecording,
      handleStopRecording,
      handleSetMaterial,
      handleSetArea,
      handleReverseEventType,
      handleSnapToCandidate,
      handleUndo,
      handleRedo,
      undoRedo.canUndo,
      undoRedo.canRedo,
      modifiedEventIds,
      hasUnsavedChanges,
      originalEventsById,
      seedEventsById,
      editedSinceSeed,
      selectedEventId,
      recordingEventId,
      restEventManagement,
    ]
  );

  return (
    <ManualReviewEventContext value={contextValue}>
      {children}
    </ManualReviewEventContext>
  );
};
